import { Breadcrumbs, Link, Typography } from "@mui/material";
import React, { useState } from "react";
import type { IpfModule } from "../..";
import { TierLevel } from "../../enums.generated";
import { TYPES } from "../../ioc/types";
import { SelectedTier, Tier } from "../../models/app";
import { UserTierService } from "../../services";
import { useInject } from "../hooks/useInject";
import { SelectTierButton } from "./SelectTierButton";

/**
 * Selected tier component options interface.
 */
export interface SelectedTierOptions {

	/**
	 * Determines whether tier 3 broker is required or not.
	 */
	requiresBroker?: boolean;

	/**
	 * Determines whether tier 2 group is required or not.
	 */
	requiresGroup?: boolean;

	/**
	 * Determines whether the tier selection component should be open by default.
	 */
	autoOpen?: boolean;

	/**
	 * Handler function for determining whether the select tier dialog should be allowed to closed / cancelled out of selection.
	 * @returns Boolean indicating whether the dialog should be closed or not.
	 */
	onDialogCancel?: () => boolean;
}

/**
 * Selected tier component, bring together the selected tier, the select tier button and the select tier dialog components together into a single component.
 * @param props Configuration properties.
 * @see SelectedTierOptions
 */
export const SelectedTierComponent = (props: SelectedTierOptions) => {

	const userTierService = useInject<UserTierService>(TYPES.UserTierService);
	const ipfModule = useInject<IpfModule>(TYPES.IpfModule);
	const currentTier = userTierService.getCurrentTier();

	// setup initial state
	const [selectedTier, setSelectedTier] = useState(currentTier || {} as SelectedTier);

	// tier selected handler function
	const tierSelected = (tier: SelectedTier) => {

		userTierService.setPageTier(tier);

		setSelectedTier(tier);

		if (ipfModule.onPageTierChanged)
			ipfModule.onPageTierChanged();
	}

	// gets a tier link for display in the tier breadcrumbs component
	const getLink = (tier: Tier | undefined, level: TierLevel) => {

		if (tier?.id) {

			// return link component with on click handler defined
			return <Link href="#" title={tier.name} onClick={(event: React.MouseEvent) => {

				// copy selected tier
				const newSelectedTier = { ...selectedTier } as SelectedTier;

				// clear appropriate tier properties
				switch (level) {
					case TierLevel.Group:
						newSelectedTier.branch = undefined;
						newSelectedTier.broker = undefined;
						break;
					case TierLevel.Branch:
						newSelectedTier.broker = undefined;
						break;
				}

				// now select the updated tier
				tierSelected(newSelectedTier);

				event.preventDefault();
			}}>
						{tier.name}
					</Link>;
		}
		else {

			return undefined;
		}
	}

	// gets the typography element for the tier breadcrumb
	const getText = (tier: Tier | undefined) => {

		if (tier?.id) {

			return <Typography><span title={tier.name}>{tier.name}</span></Typography>;
		}
		else {

			return undefined;
		}
	}

	let selectedElement: JSX.Element;
	let selectTierButton: JSX.Element | null;

	const brokerElement = getText(selectedTier.broker);

	// check if change of tier is allowed
	// if not then set broker name as selected element without any breadcrumbs
	if (userTierService.isFullyConstrained() && brokerElement) {

		selectedElement = <div>{brokerElement}</div>;
		selectTierButton = null;
	}
	else {

		// otherwise build group, branch, broker elements with a link or read only text,
		// depending on the users permissions
		const groupElement = userTierService.isAllowedToChangeGroup() ? getLink(selectedTier.group, TierLevel.Group) : getText(selectedTier.group);
		const branchElement = userTierService.isAllowedToChangeBranch() ? getLink(selectedTier.branch, TierLevel.Branch) : getText(selectedTier.branch);

		// if there any tier elements selected, build the breadcrumbs
		if (groupElement || branchElement || brokerElement) {

			selectedElement =
				<Breadcrumbs aria-label="breadcrumb" separator=">">
					{groupElement}
					{branchElement}
					{brokerElement}
				</Breadcrumbs>;
		}
		// otherwise it's finance house level
		else {

			selectedElement = <div>Finance House</div>;
		}

		// if the user is allowed to change the page tier,
		// set the select tier button
		if (userTierService.isAllowedToChangePageTier()) {

			selectTierButton =
				<SelectTierButton label="Change"
					tooltip="Change the selected tier to filter by"
					selectedTier={selectedTier}
					onSelect={tierSelected}
					{...props} />;
		}
		else {

			selectTierButton = null;
		}
	}

	// output selected tier element and select tier button (depending on permissions and current state)
	return (
		<div className="tier-navbar d-flex flex-row">
			{selectedElement}
			{selectTierButton}
		</div>
    );
}